import { useMemo } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { notEmpty } from '../homeViewsQueryHelpers.ts';

import HomeRecentWorkQuery from './__generated__/HomeRecentWorkQuery.graphql.ts';
import type {
	HomeRecentAgentSessionsQuery$data,
	HomeRecentAgentSessionsQuery$variables,
} from './AgentSessions/__generated__/HomeRecentAgentSessionsQuery.graphql.ts';
import HomeRecentAgentSessionsQuery from './AgentSessions/__generated__/HomeRecentAgentSessionsQuery.graphql.ts';
import type {
	HomeRecentAgentSessionAssociationsQuery$data,
	HomeRecentAgentSessionAssociationsQuery$variables,
} from './AgentSessions/__generated__/HomeRecentAgentSessionAssociationsQuery.graphql.ts';
import HomeRecentAgentSessionAssociationsQuery from './AgentSessions/__generated__/HomeRecentAgentSessionAssociationsQuery.graphql.ts';
import type {
	HomeRecentRovoActivationQuery$data,
	HomeRecentRovoActivationQuery$variables,
} from './AgentSessions/__generated__/HomeRecentRovoActivationQuery.graphql.ts';
import HomeRecentRovoActivationQuery from './AgentSessions/__generated__/HomeRecentRovoActivationQuery.graphql.ts';
import type {
	RecentListHomeRecentWorkV3Query as RecentListHomeRecentWorkV3Query$data,
	RecentListHomeRecentWorkV3QueryVariables as RecentListHomeRecentWorkV3Query$variables,
	RecentListHomeRecentWorkV3Query_Query,
} from './__types__/RecentListHomeRecentWorkV3Query.ts';
import {
	getRecentAgentSessionAssociationsQueryVariables,
	getRecentAgentSessionsQueryVariables,
	RECENT_AGENT_SESSIONS_DISPLAY_COUNT,
} from './AgentSessions/recentAgentSessionsQueryHelpers.ts';
import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers.ts';
import type { AgentSessionAssociation } from './AgentSessions/useAgentSessionResumeProps.ts';
import { useHomeRecentAgentSessionsSubscription } from './AgentSessions/useHomeRecentAgentSessionsSubscription.ts';

export const useHomeRecentWorkData = ({
	isWhiteboardViewedEnabled,
	isDatabaseEnabled,
	isSlidesEnabled,
	first,
}: {
	isWhiteboardViewedEnabled: boolean;
	isDatabaseEnabled: boolean;
	isSlidesEnabled?: boolean;
	first?: number;
}): {
	data: RecentListHomeRecentWorkV3Query_Query | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const { cloudId, isLoggedIn } = useSessionData();
	const { data, loading, error } = useQueryInterop<
		RecentListHomeRecentWorkV3Query$data,
		RecentListHomeRecentWorkV3Query$variables
	>(HomeRecentWorkQuery, {
		...getRecentWorkQueryOptions({
			cloudId,
			isLoggedIn,
			isWhiteboardViewedEnabled,
			isDatabaseEnabled,
			isSlidesEnabled,
			first,
		}),
		interopApolloAggClient: true,
	});

	return { data, loading, error };
};

export type HomeRecentAgentSession = NonNullable<
	NonNullable<NonNullable<HomeRecentAgentSessionsQuery$data['agent_sessionQuery']>['nodes']>[number]
>;

export const useHomeRecentAgentSessionsData = ({
	first,
}: {
	first?: number;
} = {}): {
	data: HomeRecentAgentSession[];
	associationsBySessionId: HomeRecentAgentSessionAssociationsData;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const { cloudId, isLoggedIn, userId } = useSessionData();
	const rovoActivationQueryResult = useQueryInterop<
		HomeRecentRovoActivationQuery$data,
		HomeRecentRovoActivationQuery$variables
	>(HomeRecentRovoActivationQuery, {
		variables: { cloudId },
		fetchPolicy: 'cache-first',
		interopApolloAggClient: true,
		skip: !isLoggedIn || !userId,
	});

	const rovoActivationId =
		rovoActivationQueryResult.data?.tenantContexts?.[0]?.activationIdByProduct?.active;

	const skip = !isLoggedIn || !userId || !rovoActivationId;
	const agentSessionsQueryResult = useQueryInterop<
		HomeRecentAgentSessionsQuery$data,
		HomeRecentAgentSessionsQuery$variables
	>(HomeRecentAgentSessionsQuery, {
		variables: skip
			? { aqlQuery: '', first: 1, shardingContext: '' }
			: getRecentAgentSessionsQueryVariables({
					userId,
					rovoActivationId,
					first,
				}),
		interopApolloAggClient: true,
		skip,
	});

	const agentSessionsData = useMemo(
		() =>
			(agentSessionsQueryResult.data?.agent_sessionQuery?.nodes ?? [])
				.filter(notEmpty)
				.slice(0, RECENT_AGENT_SESSIONS_DISPLAY_COUNT),
		[agentSessionsQueryResult.data?.agent_sessionQuery?.nodes],
	);

	const associationsBySessionId = useHomeRecentAgentSessionAssociationsData({
		agentSessionsData,
		rovoActivationId,
	});

	useHomeRecentAgentSessionsSubscription(agentSessionsQueryResult, rovoActivationId);

	return {
		data: agentSessionsData,
		loading: rovoActivationQueryResult.loading || agentSessionsQueryResult.loading,
		error: rovoActivationQueryResult.error || agentSessionsQueryResult.error,
		associationsBySessionId,
	};
};

const useHomeRecentAgentSessionAssociationsData = ({
	agentSessionsData,
	rovoActivationId,
}: {
	agentSessionsData: readonly HomeRecentAgentSession[];
	rovoActivationId: string | null | undefined;
}): HomeRecentAgentSessionAssociationsData => {
	const { userId } = useSessionData();

	const associatedEntities = useMemo(
		() =>
			agentSessionsData.flatMap((session) =>
				session.id
					? (session.associatedEntities ?? []).flatMap((entity) =>
							entity?.__typename === 'ConfluencePage'
								? [{ entityId: entity.id, sessionId: session.id! }]
								: [],
						)
					: [],
			),
		[agentSessionsData],
	);

	const skip = !rovoActivationId || !userId || associatedEntities.length === 0;
	const { data, loading, error } = useQueryInterop<
		HomeRecentAgentSessionAssociationsQuery$data,
		HomeRecentAgentSessionAssociationsQuery$variables
	>(HomeRecentAgentSessionAssociationsQuery, {
		variables: skip
			? { aqlQuery: '', first: 1, shardingContext: '' }
			: getRecentAgentSessionAssociationsQueryVariables({
					associatedEntities,
					rovoActivationId,
					userId,
				}),
		interopApolloAggClient: true,
		skip,
	});

	const associationsBySessionId = useMemo(() => {
		const bySessionId: Record<string, AgentSessionAssociation[]> = {};
		for (const node of data?.agent_sessionAssociationQuery?.nodes ?? []) {
			const association = {
				invocationType: node?.invocationType,
				subEntityId: node?.subEntityId,
			};
			// Keyed under both ids because either can be the one the session node reports.
			// They are the same value today, so the Set stops a single association being recorded twice.
			const sessionIdsForNode = new Set(
				[node?.agentSession?.id, node?.conversationId].filter((sessionId): sessionId is string =>
					Boolean(sessionId),
				),
			);
			for (const sessionId of sessionIdsForNode) {
				bySessionId[sessionId] = [...(bySessionId[sessionId] ?? []), association];
			}
		}
		return bySessionId;
	}, [data]);

	return {
		data: associationsBySessionId,
		loading,
		error,
	};
};

export type HomeRecentAgentSessionAssociationsData = {
	data: Record<string, AgentSessionAssociation[]>;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
};
